import type { gsap as GsapType } from 'gsap';
import type { ScrollTrigger as ScrollTriggerType } from 'gsap/ScrollTrigger';

/**
 * GSAP is loaded after first paint, never before it.
 *
 * None of the scroll choreography is needed to render the hero, and having
 * GSAP plus ScrollTrigger in the entry chunk put their parse and execute cost
 * in front of the Largest Contentful Paint.
 */
export interface GsapBundle {
  gsap: typeof GsapType;
  ScrollTrigger: typeof ScrollTriggerType;
}

let pending: Promise<GsapBundle> | null = null;

export function loadGsap(): Promise<GsapBundle> {
  pending ??= Promise.all([import('gsap'), import('gsap/ScrollTrigger')]).then(
    ([core, scroll]) => {
      const gsap = core.gsap ?? core.default;
      const { ScrollTrigger } = scroll;

      gsap.registerPlugin(ScrollTrigger);

      // A phone shows and hides its URL bar constantly while the reader scrolls,
      // and each of those resizes refreshes every pin on the page by default.
      // The viewport width is what the layout actually depends on here.
      ScrollTrigger.config({ ignoreMobileResize: true });

      return { gsap, ScrollTrigger };
    },
  );

  return pending;
}

/**
 * One place decides whether scroll choreography runs at all.
 *
 * `gsap.matchMedia` handles the reduced-motion branch for every sequence on the
 * page, so a reader with motion turned down never lands on a pinned section
 * that never unpins.
 */
export const motionQuery = {
  full: '(prefers-reduced-motion: no-preference)',
  reduced: '(prefers-reduced-motion: reduce)',
} as const;
